import { checkIfOpen } from './checker';

/**
 * 拉起的方法
 */
export interface LaunchMethodOptions {
  /**
   * 跳转延迟
   */
  timeout?: number;
  /**
   * 是否为打开App的链接，如果只是一个普通的http链接则设置为false
   *
   * 默认为true，这时候会在打开链接后对页面的可见性进行监听从而判断是否拉起app
   *
   * 设置为false后函数始终返回false的值
   */
  isCheck?: boolean;
  /**
   * 检测应用是否有被打开的延迟，默认3000毫秒
   */
  checkDelay?: number;
}

export interface LaunchHooks {
  beforeStart?: (url: string, methods: string) => unknown;
}


export type LaunchMethod = (url: string, options?: LaunchMethodOptions, hooks?: LaunchHooks) => Promise<boolean>;

interface LaunchMethods {
  launchByIframe: LaunchMethod
  launchByLocation: LaunchMethod
  launchByTagA: LaunchMethod;
}

/**
 * 使用此类可选择iframe,location,a标签的方式拉起app
 */
export class Launcher implements LaunchMethods {
  /**
   * 检测APP拉起的默认延迟
   */
  public static defaultCheckDelay = 3000;
  /**
   * 默认行为为拉起App，如果设置为false，则不会对应用的拉起进行检测
   */
  public static defaultCheck = true;
  /**
   * 开始调用跳转之前的延迟，默认为undefined
   */
  public static defaultTimeout?: number;

  public static elIframeId = 'applauncher-iframe-launcher';
  public static elAnchorId = 'applauncher-anchor-launcher';
  /**
   * 保存对iframe的引用
   */
  private static elIframe?: HTMLIFrameElement;
  /**
   * 保存对a标签的引用
   */
  private static elAnchor?: HTMLAnchorElement;

  /**
   * 使用iframe的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public static async launchByIframe(
    url: string,
    options?: LaunchMethodOptions,
    hooks?: LaunchHooks,
  ): Promise<boolean> {
    const {
      checkDelay,
      timeout,
      isCheck: isCheck,
    } = Launcher.getLaunchOptions(options);
    if (timeout !== undefined) {
      await Launcher.sleep(timeout);
    }

    hooks?.beforeStart?.(url, 'byIframe');

    if (!Launcher.elIframe) {
      Launcher.elIframe = document.createElement('iframe');
      Launcher.elIframe.id = Launcher.elIframeId;
      Launcher.elIframe.style.cssText = 'display:none;';
      document.body.appendChild(Launcher.elIframe);
    }
    Launcher.elIframe.src = url;

    if (!isCheck) {
      return false;
    }
    return Promise.resolve(checkIfOpen(checkDelay));
  }

  /**
   * 使用<a>标签的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public static async launchByTagA(url: string, options?: LaunchMethodOptions, hooks?: LaunchHooks): Promise<boolean> {
    const {
      checkDelay,
      timeout,
      isCheck: isCheck,
    } = Launcher.getLaunchOptions(options);
    if (timeout !== undefined) {
      await Launcher.sleep(timeout);
    }
    hooks?.beforeStart?.(url, 'byTagA');
    if (!Launcher.elAnchor) {
      Launcher.elAnchor = document.createElement('a');
      Launcher.elAnchor.style.display = 'none';
      Launcher.elAnchor.id = Launcher.elAnchorId;
      document.body.appendChild(Launcher.elAnchor);
    }
    Launcher.elAnchor.href = url;
    // 手动调用click方法
    Launcher.elAnchor.click();
    if (!isCheck) {
      return false;
    }
    return Promise.resolve(checkIfOpen(checkDelay));
  }

  /**
   * 使用location的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public static async launchByLocation(
    url: string,
    options?: LaunchMethodOptions,
    hooks?: LaunchHooks,
  ): Promise<boolean> {
    const {
      checkDelay,
      timeout,
      isCheck: isCheck,
    } = Launcher.getLaunchOptions(options);
    if (timeout !== undefined) {
      await Launcher.sleep(timeout);
    }
    hooks?.beforeStart?.(url, 'byLocation');
    window.location.href = url;

    if (!isCheck) {
      return false;
    }
    return Promise.resolve(checkIfOpen(checkDelay));
  }

  public static sleep(timeout: number): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, timeout);
    });
  }

  private static getLaunchOptions(options?: LaunchMethodOptions): LaunchMethodOptions {
    return {
      checkDelay: options?.checkDelay ?? Launcher.defaultCheckDelay,
      isCheck: options?.isCheck ?? Launcher.defaultCheck,
      timeout: options?.timeout ?? Launcher.defaultTimeout,
    };
  }

  private options: LaunchMethodOptions;

  private hooks?: LaunchHooks;

  /**
   * @param options - 实例的启动配置
   *
   */
  public constructor(options?: LaunchMethodOptions, hooks?: LaunchHooks) {
    this.options = Launcher.getLaunchOptions(options);
    this.hooks = hooks;
  }

  /**
   * 使用location的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public launchByLocation(url: string, options?: LaunchMethodOptions, hooks?: LaunchHooks): Promise<boolean> {
    return Launcher.launchByLocation(url, this.mergeOptions(options), this.mergeHooks(hooks));
  }

  /**
   * 使用<a>标签的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public launchByTagA(url: string, options?: LaunchMethodOptions, hooks?: LaunchHooks): Promise<boolean> {
    return Launcher.launchByTagA(url, this.mergeOptions(options), this.mergeHooks(hooks));
  }


  /**
   * 使用iframe的方式打开链接
   * @param url - 链接
   * @param options - 选项
   * @param hooks - 钩子
   */
  public launchByIframe(url: string, options?: LaunchMethodOptions, hooks?: LaunchHooks): Promise<boolean> {
    return Launcher.launchByIframe(url, this.mergeOptions(options), this.mergeHooks(hooks));
  }

  private mergeOptions(options?: LaunchMethodOptions) {
    return {
      ...this.options,
      ...options,
    };
  }

  private mergeHooks(hooks?: LaunchHooks) {
    return {
      ...this.hooks,
      ...hooks,
    };
  }
}


